import React, { useRef, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import fr from "@/content/fr";
import { trackViewContent } from "@/lib/analytics";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  const reduce = useReducedMotion();
  const videoRef = useRef(null);

  // React applique mal l'attribut `muted` -> l'autoplay est bloqué. On force ici.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.setAttribute("muted", "");
    const tryPlay = () => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  const onPrimary = () => {
    trackViewContent({ content_name: "hero_cta_discover" });
    scrollToId("expo");
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center"
    >
      {/* Atmospheric background layers */}
      <div className="absolute inset-0 -z-10" aria-hidden="true">
        {/* Base deep amethyst background */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(115% 115% at 72% 46%, #4C3B72 0%, #382457 36%, #251741 66%, #160B2A 100%)",
          }}
        />
        {/* Floating sparkle particles */}
        <div className="absolute inset-0">
          {Array.from({ length: 22 }).map((_, i) => {
            const left = (i * 37) % 100;
            const top = (i * 71) % 100;
            const size = (i % 3) + 1;
            const dur = 4 + ((i * 1.3) % 5);
            const delay = (i * 0.27) % 3;
            return (
              <motion.span
                key={i}
                className="absolute rounded-full bg-white"
                style={{
                  left: `${left}%`,
                  top: `${top}%`,
                  width: size,
                  height: size,
                  filter: "drop-shadow(0 0 4px rgba(255,255,255,0.9))",
                }}
                animate={reduce ? { opacity: 0.6 } : { opacity: [0.1, 0.85, 0.1], scale: [0.6, 1.2, 0.6] }}
                transition={{ duration: dur, delay, ease: "easeInOut", repeat: Infinity }}
              />
            );
          })}
        </div>
        {/* Film-grain overlay */}
        <div
          className="absolute inset-0 opacity-[0.10] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.5) 0.6px, transparent 0.6px)",
            backgroundSize: "3px 3px",
          }}
        />
        {/* Bottom fade into next section */}
        <div
          className="absolute bottom-0 left-0 right-0 h-40"
          style={{
            background:
              "linear-gradient(to bottom, transparent, #15042E 90%)",
          }}
        />
      </div>

      <div className="relative w-full max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12 pt-32 pb-24 sm:pt-36 lg:pt-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <motion.p
              initial={{ y: 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="inline-flex items-center gap-2 text-[11px] sm:text-xs tracking-[0.34em] uppercase text-white/65 font-mono"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-6 h-px bg-white/50" />
              {fr.hero.eyebrow}
            </motion.p>

            <motion.h1
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="font-display font-extrabold text-white uppercase mt-5 leading-[0.92] tracking-[-0.01em]"
              style={{ fontSize: "clamp(2.75rem, 8vw, 6rem)" }}
              data-testid="hero-headline"
            >
              {fr.hero.headline.split(" ").map((w, i) => (
                <motion.span
                  key={i}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.25 + i * 0.06 }}
                  className="inline-block mr-[0.22em] last:mr-0"
                >
                  {w}
                </motion.span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-7 max-w-[640px] text-[15.5px] sm:text-[17px] leading-[1.7] text-white/72"
              data-testid="hero-subtitle"
            >
              {fr.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={onPrimary}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-[#E91E63] hover:bg-[#ff2974] text-white font-display font-extrabold uppercase tracking-[0.12em] text-[15px] shadow-[0_0_0_0_rgba(233,30,99,0.0)] hover:shadow-[0_0_44px_4px_rgba(233,30,99,0.55)] transition-all duration-300 hover:-translate-y-[2px] active:translate-y-0"
              >
                {fr.hero.cta}
                <ArrowDown size={18} className="transition-transform group-hover:translate-y-[2px]" />
              </button>

              <div className="hidden sm:flex items-center gap-3 pl-2 text-white/55 text-xs tracking-[0.24em] uppercase font-mono">
                <span className="inline-block w-8 h-px bg-white/30" />
                {fr.hero.scrollHint}
              </div>
            </motion.div>
          </div>

          {/* Rotating gem — "exposé" dans un halo */}
          <div className="lg:col-span-5 order-1 lg:order-2 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.15, ease: "easeOut" }}
              className="relative w-[260px] h-[260px] sm:w-[340px] sm:h-[340px] lg:w-[460px] lg:h-[460px]"
            >
              <div
                className="absolute inset-0 rounded-full blur-3xl opacity-70"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(186,120,255,0.45), rgba(233,30,99,0.18) 45%, transparent 70%)",
                }}
              />
              <video
                ref={videoRef}
                className="relative w-full h-full object-cover"
                style={{
                  WebkitMaskImage:
                    "radial-gradient(closest-side, #000 58%, transparent 100%)",
                  maskImage:
                    "radial-gradient(closest-side, #000 58%, transparent 100%)",
                }}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/images/gem-poster.jpg"
                aria-hidden="true"
              >
                <source src="/video/gem.webm" type="video/webm" />
                <source src="/video/gem-opt.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
